import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@pella/sash-framework';
import { setRequestCommunicationInCache } from '../../utils';
import { createCommunication } from '../../api/production-request-api/index.js';
import { useAppContext } from '../../providers';
import { QUERY_KEYS } from '../../data/constants';

/**
 * Hook containing mutation function for appending a communication to a Production Request.
 *
 * The API builds the entry and returns the request's full communication history, so the
 * username and timestamp are server-owned. The cached request lists are updated on success
 * so the open drawer and the card behind it reflect the new entry without a refetch.
 */
export const useAddCommunication = () => {
  const { selectedResource, selectedLocation } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation({
    /**
     * mutateFn - Appends a communication entry to the request.
     * @param {object} params
     * @param {Local.RequestWithCommunication} params.request - The request being commented on.
     * @param {string} params.text - The communication text entered by the user.
     * @returns {Promise<Local.Communication[]>} The full communication history that was saved.
     */
    mutationFn: async ({ request, text }) => {
      const saved = await createCommunication(request.id, text);

      return saved.communications ?? [];
    },
    onSuccess: (communication, variables) => {
      if (selectedResource)
        queryClient.setQueryData(
          [QUERY_KEYS.REQUESTS_BY_RESOURCE, selectedResource],
          (/** @type {Pella.ProductionRequestAPI.GetRequestsOutput} */ oldData) =>
            setRequestCommunicationInCache(oldData, variables.request.id, communication),
        );

      if (selectedLocation)
        queryClient.setQueryData(
          [QUERY_KEYS.REQUESTS_BY_LOCATION, selectedLocation],
          (/** @type {Pella.ProductionRequestAPI.GetRequestsOutput} */ oldData) =>
            setRequestCommunicationInCache(oldData, variables.request.id, communication),
        );
    },
    onError: () => toast.error('Error saving communication.'),
  });
};
